import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSuperadminAuth } from '../context/SuperadminAuthContext';
import { filterMenuItems } from '../utils/menuAccess';
import './SuperadminLayout.css';

// 'Utilisateurs' est volontairement absent du catalogue de permissions
// (preloteserver/menu_catalog.py) — un compte staff limite ne peut jamais
// gerer d'autres comptes staff, meme si ce lien restait dans ce tableau. Pas
// besoin de le retirer manuellement pour un compte restreint : filterMenuItems
// l'exclut deja tout seul (son path n'apparaitra jamais dans admin.menu_keys,
// impossible a accorder via l'API cf. AdminStaffUserSerializer.validate_menu_keys).
const superadminNavItems = [
  { label: 'Dashboard', path: '/superadmin/dashboard' },
  { label: 'Companies', path: '/superadmin/companies' },
  { label: 'Tirages', path: '/superadmin/tirages' },
  { label: 'Devices', path: '/superadmin/devices' },
  { label: 'Banques / POS', path: '/superadmin/banks' },
  { label: 'Numeros gagnants', path: '/superadmin/winning-numbers' },
  { label: 'Avis', path: '/superadmin/avis' },
  { label: 'Transferts', path: '/superadmin/transfers' },
  { label: 'Recherche Ticket', path: '/superadmin/ticket-search' },
  { label: 'Rapports', path: '/superadmin/reports' },
  { label: 'Facturation', path: '/superadmin/facturation' },
  { label: 'Paiements en ligne', path: '/superadmin/payment-submissions' },
  { label: "Journal d'audit", path: '/superadmin/audit-log' },
  { label: 'Déblocages kiosque', path: '/superadmin/kiosk-requests' },
  { label: 'Utilisateurs', path: '/superadmin/staff-users' },
];

const SuperadminLayout = ({ children }) => {
  const { admin, logout } = useSuperadminAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Mobile/tablette (< 900px, cf. SuperadminLayout.css) : menu replie derriere
  // un bouton "☰ Menu" au lieu de 13 liens sur une ligne qui debordait
  // (2026-09-24, "le responsive ne marche pas du tout"). Il s'ouvre en TIROIR
  // lateral par-dessus la page (fond assombri), sans pousser le contenu vers
  // le bas (2e demande explicite). Referme au changement de page, au clic sur
  // le fond, sur ✕ ou avec Echap ; la page derriere ne defile pas pendant ce temps.
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [pathname]);
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const visibleNavItems = useMemo(
    () => filterMenuItems(superadminNavItems, admin?.menu_keys),
    [admin?.menu_keys],
  );

  const handleLogout = async () => {
    await logout();
    navigate('/superadmin/login');
  };

  return (
    <div className="sa-layout">
      <div className="sa-topbar">
        <span className="sa-brand">
          <img className="sa-logo" src="/logo-cgl-transparent.png" alt="CGL Multi-services" />
          Superadmin
        </span>
        <div className="sa-topbar-right">
          {admin && <span className="sa-username">{admin.username}</span>}
          <span className="sa-logout" onClick={handleLogout}>Deconnexion</span>
        </div>
      </div>
      <nav className="sa-nav">
        <button
          type="button"
          className="sa-nav-toggle"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          ☰ Menu
        </button>
        <div
          className={`sa-nav-backdrop${menuOpen ? ' sa-nav-backdrop-open' : ''}`}
          onClick={() => setMenuOpen(false)}
          aria-hidden="true"
        />
        <div className={`sa-nav-links${menuOpen ? ' sa-nav-links-open' : ''}`}>
          <div className="sa-drawer-header">
            <span className="sa-drawer-title">Superadmin</span>
            <button
              type="button"
              className="sa-drawer-close"
              aria-label="Fermer le menu"
              onClick={() => setMenuOpen(false)}
            >
              ✕
            </button>
          </div>
          {visibleNavItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`sa-nav-link${pathname.startsWith(item.path) ? ' sa-nav-link-active' : ''}`}
            >
              {item.label}
            </Link>
          ))}
        </div>
      </nav>
      <div className="sa-body">{children}</div>
    </div>
  );
};

export default SuperadminLayout;
